/**
 * AuditLogPage - Platform Audit Log Viewer
 *
 * Query interface for browsing platform audit events with filters,
 * pagination, expandable details, and CSV export.
 *
 * Uses API_ENDPOINTS.SUPERADMIN.AUDIT (superadminAuditLogGet).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  TextInput,
  Select,
  Table,
  Badge,
  Spinner,
} from 'flowbite-react';
import { HiSearch, HiDownload, HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { Make_Authorized_API_Call } from '@equilateral/ui-shared';
import { useStatus } from '../contexts/StatusProvider';
import { API_ENDPOINTS } from '../services/api/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditRecord {
  audit_id: string;
  user_email: string;
  action: string;
  target_type: string;
  target_id: string;
  details: Record<string, unknown> | string | null;
  ip_address: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_OPTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'APPROVE',
  'REJECT',
  'EXPORT',
  'IMPORT',
  'EXECUTE',
  'IMPERSONATE',
];

const TARGET_TYPE_OPTIONS = [
  'client',
  'company',
  'user',
  'specification',
  'standard',
  'agent',
  'project',
  'policy',
  'workflow',
  'billing',
];

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString();
}

function buildCsvContent(records: AuditRecord[]): string {
  const headers = [
    'Timestamp',
    'User Email',
    'Action',
    'Target Type',
    'Target ID',
    'Details',
    'IP Address',
  ];

  const rows = records.map((r) => [
    escapeCsv(r.created_at ? new Date(r.created_at).toISOString() : ''),
    escapeCsv(r.user_email),
    escapeCsv(r.action),
    escapeCsv(r.target_type),
    escapeCsv(r.target_id),
    escapeCsv(typeof r.details === 'object' ? JSON.stringify(r.details) : String(r.details || '')),
    escapeCsv(r.ip_address),
  ]);

  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

function escapeCsv(value: string): string {
  if (!value) return '';
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

function triggerDownload(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AuditLogPage: React.FC = () => {
  const { Client_ID } = useStatus();

  // Data
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Pagination
  const [offset, setOffset] = useState(0);

  // Expanded row
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (Client_ID) params.append('Client_ID', Client_ID);
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(offset));
      if (emailFilter) params.append('email', emailFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (targetTypeFilter) params.append('target_type', targetTypeFilter);
      if (dateStart) params.append('start_date', dateStart);
      if (dateEnd) params.append('end_date', dateEnd);

      const url = `${API_ENDPOINTS.SUPERADMIN.AUDIT}?${params.toString()}`;
      const response = await Make_Authorized_API_Call(url, 'GET');

      if (!response?.success) {
        throw new Error((response as any)?.error_details || 'Failed to fetch audit log');
      }

      setRecords(response.data?.Records || []);
      setTotalCount(response.meta?.Total_Records ?? (response.data?.Records || []).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [Client_ID, offset, emailFilter, actionFilter, targetTypeFilter, dateStart, dateEnd]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSearch = () => {
    setOffset(0);
    fetchAuditLog();
  };

  const handleClearFilters = () => {
    setEmailFilter('');
    setActionFilter('');
    setTargetTypeFilter('');
    setDateStart('');
    setDateEnd('');
    setOffset(0);
  };

  const handleExportCsv = () => {
    if (records.length === 0) return;
    const csv = buildCsvContent(records);
    const filename = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    triggerDownload(csv, filename);
  };

  const toggleRow = (id: string) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const handleNextPage = () => setOffset((prev) => prev + PAGE_SIZE);
  const handlePrevPage = () => setOffset((prev) => Math.max(0, prev - PAGE_SIZE));

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Log</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totalCount} event{totalCount !== 1 ? 's' : ''} found
          </p>
        </div>
        <Button color="blue" onClick={handleExportCsv} disabled={records.length === 0}>
          <HiDownload className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
            <TextInput
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              sizing="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
            <TextInput
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              sizing="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User Email</label>
            <TextInput
              icon={HiSearch}
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Filter by email..."
              sizing="sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              sizing="sm"
            >
              <option value="">All Actions</option>
              {ACTION_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Target Type</label>
            <Select
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              sizing="sm"
            >
              <option value="">All Types</option>
              {TARGET_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" color="blue" onClick={handleSearch}>
            <HiSearch className="mr-1 h-4 w-4" />
            Search
          </Button>
          <Button size="sm" color="light" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-500">Loading audit events...</span>
        </div>
      )}

      {/* Results Table */}
      {!loading && records.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell className="w-8"></Table.HeadCell>
                <Table.HeadCell>Timestamp</Table.HeadCell>
                <Table.HeadCell>User Email</Table.HeadCell>
                <Table.HeadCell>Action</Table.HeadCell>
                <Table.HeadCell>Target Type</Table.HeadCell>
                <Table.HeadCell>Target ID</Table.HeadCell>
                <Table.HeadCell>IP Address</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {records.map((record) => (
                  <React.Fragment key={record.audit_id}>
                    <Table.Row
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleRow(record.audit_id)}
                    >
                      <Table.Cell>
                        {expandedRow === record.audit_id ? (
                          <HiChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <HiChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-xs whitespace-nowrap">
                        {formatTimestamp(record.created_at)}
                      </Table.Cell>
                      <Table.Cell className="text-sm">{record.user_email || '--'}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={
                            record.action === 'DELETE'
                              ? 'failure'
                              : record.action === 'CREATE'
                                ? 'success'
                                : record.action === 'UPDATE'
                                  ? 'warning'
                                  : 'info'
                          }
                        >
                          {record.action}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="text-sm">{record.target_type || '--'}</Table.Cell>
                      <Table.Cell className="text-xs font-mono max-w-[200px] truncate">
                        {record.target_id || '--'}
                      </Table.Cell>
                      <Table.Cell className="text-xs">{record.ip_address || '--'}</Table.Cell>
                    </Table.Row>

                    {/* Expanded details row */}
                    {expandedRow === record.audit_id && (
                      <Table.Row>
                        <Table.Cell colSpan={7}>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">
                              Details:
                            </span>
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-48 bg-white p-2 rounded border">
                              {record.details
                                ? typeof record.details === 'object'
                                  ? JSON.stringify(record.details, null, 2)
                                  : String(record.details)
                                : 'No details available.'}
                            </pre>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                ))}
              </Table.Body>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                color="light"
                onClick={handlePrevPage}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <span className="flex items-center text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                size="sm"
                color="light"
                onClick={handleNextPage}
                disabled={offset + PAGE_SIZE >= totalCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && records.length === 0 && (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg font-medium mb-2">No audit events found</p>
          <p className="text-sm">Try adjusting your filters or date range.</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
